/**
 * Loads a raw .pptx from `file.cdnPath` and paints it with SlideDeckViewer.
 * Used by the desktop pptx-preview sidebar and the full-screen artifact viewer.
 */
import React, { useEffect, useState } from 'react';
import { ActivityIndicator, Pressable, StyleSheet, View } from 'react-native';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { useTheme } from '@/context/ThemeContext';
import { openFileExternally } from '@/constants/utils/files';
import { presentationSpecFromPptx, revokePptxObjectUrls } from '@/constants/utils/pptx_preview';
import SlideDeckViewer from '@/components/Files/SlideDeckViewer';

export default function PptxDeckViewer({ file }) {
  const { colors } = useTheme();
  const source = file?.cdnPath || file?.url;
  const [status, setStatus] = useState(source ? 'loading' : 'error');
  const [viewerFile, setViewerFile] = useState(null);

  useEffect(() => {
    if (!source) {
      setStatus('error');
      setViewerFile(null);
      return undefined;
    }

    let cancelled = false;
    let objectUrls = [];
    setStatus('loading');
    setViewerFile(null);

    (async () => {
      try {
        const response = await fetch(source);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.arrayBuffer();
        const parsed = await presentationSpecFromPptx(data, {
          fallbackTitle: file?.name || file?.originalName || 'Presentation',
        });
        if (cancelled) {
          revokePptxObjectUrls(parsed.objectUrls);
          return;
        }
        objectUrls = parsed.objectUrls;
        setViewerFile({
          ...file,
          parameters: {
            ...(file?.parameters || {}),
            title: parsed.spec.title,
            presentationSpec: parsed.spec,
          },
        });
        setStatus('ready');
      } catch (_) {
        if (!cancelled) setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
      revokePptxObjectUrls(objectUrls);
    };
  }, [source, file?.name, file?.originalName]);

  if (status === 'ready' && viewerFile) {
    return <SlideDeckViewer file={viewerFile} preserveLayout />;
  }

  return (
    <View style={styles.status}>
      {status === 'loading' ? (
        <ActivityIndicator size="small" color={colors.text_color_secondary} />
      ) : (
        <Icon name="file-powerpoint" size={28} color={colors.text_color_secondary} />
      )}
      <Text align="center" color="secondary">
        {status === 'loading' ? 'Loading presentation…' : 'This presentation could not be previewed.'}
      </Text>
      {status === 'error' && source ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Open presentation file"
          onPress={() => openFileExternally(file)}
          style={[styles.button, { backgroundColor: colors.solid_color_secondary }]}
        >
          <Icon name="arrow-up-right-from-square" size={13} color={colors.text_color_primary} />
          <Text size="s">Open file</Text>
        </Pressable>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  status: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    padding: 24,
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 14,
    height: 40,
    borderRadius: 8,
    marginTop: 4,
  },
});
